import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import { Points } from 'components/Points/Points';
import { FoilCardImage } from 'components/FoilCardImage';
import { AssetUrls } from 'config/assets';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { useLazyGetCardByIdQuery } from 'store/services/cards/search/cardsSearchApi';
import { SidebarTradeCard } from '../../context/ProfileTradeSidebarContext';
import { SetDetail } from 'store/services/trade/tradeApi';
import { SUPPORTED_LANGUAGES, getLanguageDisplayName, formatLanguage, normalizeLanguageCode, BULK_TIER_POINTS_THRESHOLD } from 'constants/languages';

interface TradeSidebarCardProps {
  card: SidebarTradeCard;
  onUpdate: (id: string, updates: Partial<SidebarTradeCard>) => void;
  onRemove: (id: string) => void;
  onCalculatingChange?: (id: string, isCalculating: boolean) => void;
}

// Condition options
const CONDITION_OPTIONS = [
  { value: 'NEAR_MINT', label: 'NM' },
  { value: 'SLIGHTLY_PLAYED', label: 'LP' },
  { value: 'MODERATELY_PLAYED', label: 'MP' },
];

// Language options
const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map(l => ({
  value: l.code.toLowerCase(),
  label: l.abbreviation,
}));

export const TradeSidebarCard: React.FC<TradeSidebarCardProps> = ({
  card,
  onUpdate,
  onRemove,
  onCalculatingChange,
}) => {
  const [calculatePrice] = useCalculatePriceMutation();
  const [triggerGetCard] = useLazyGetCardByIdQuery();
  const [isCalculating, setIsCalculating] = useState(false);
  const hasInitialFetch = useRef(false);

  // Always recalculate price on mount to ensure displayed price matches card parameters
  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchNewPrice({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deduplicate acceptableSetDetails by setCode + collectorNumber so each
  // set appears once in the dropdown even when multiple language printings exist
  const dedupedSetDetails = useMemo(() => {
    if (!card.acceptableSetDetails) return [];
    const seen = new Map<string, SetDetail>();
    for (const sd of card.acceptableSetDetails) {
      const key = `${sd.code}-${sd.collectorNumber || ''}`;
      if (!seen.has(key)) {
        seen.set(key, sd);
      }
    }
    return Array.from(seen.values());
  }, [card.acceptableSetDetails]);

  // Get small image URL from the card's image
  const getSmallImageUrl = (url: string): string => {
    return url
      ?.replace('/large/', '/small/')
      ?.replace('/normal/', '/small/') || url;
  };

  // Calculate price from server when card properties change
  const fetchNewPrice = async (updates: Partial<SidebarTradeCard>) => {
    const updatedCard = { ...card, ...updates };

    try {
      setIsCalculating(true);
      onCalculatingChange?.(card.id, true);
      const result = await calculatePrice({
        cards: [{
          wantListId: updatedCard.wantListId,
          cardId: updatedCard.cardId?.toString(),
          condition: updatedCard.condition,
          language: updatedCard.language,
          foil: updatedCard.foil,
          set: updatedCard.set,
        }]
      }).unwrap();

      const cardResult = result.cards[0];
      const newPrice = cardResult?.displayPrice;
      if (newPrice !== undefined) {
        onUpdate(card.id, {
          ...updates,
          pointValue: newPrice,
          languageMultiplierApplied: cardResult?.languageMultiplierApplied ?? false,
          multiplierValue: cardResult?.multiplierValue ?? 1.0,
        });
      } else {
        onUpdate(card.id, updates);
      }
    } catch (error) {
      console.error('Failed to calculate price:', error);
      onUpdate(card.id, updates);
    } finally {
      setIsCalculating(false);
      onCalculatingChange?.(card.id, false);
    }
  };

  // Handle set change
  const handleSetChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    const selectedSetDetail = dedupedSetDetails.find(s =>
      `${s.code}-${s.collectorNumber || ''}` === selectedValue
    );

    if (selectedSetDetail) {
      const newAvailableInFoil = selectedSetDetail.availableInFoil ?? card.availableInFoil;
      const newAvailableInNonFoil = selectedSetDetail.availableInNonFoil ?? card.availableInNonFoil;

      const updates: Partial<SidebarTradeCard> = {
        setCode: selectedSetDetail.code,
        set: selectedSetDetail.name,
        availableInFoil: newAvailableInFoil,
        availableInNonFoil: newAvailableInNonFoil,
      };

      if (selectedSetDetail.cardId) {
        updates.cardId = selectedSetDetail.cardId;
      }

      if (selectedSetDetail.imageUrl) {
        updates.imageUrl = selectedSetDetail.imageUrl;
      }

      // Fetch per-set availableLanguages via getCardById
      // The backend scopes availableLanguages by oracleId + setCode + collectorNumber,
      // so fetching by the new set's scryfallId returns only languages for that set
      const selectedScryfallId = selectedSetDetail.scryfallId;
      if (selectedScryfallId) {
        try {
          const cardData = await triggerGetCard(selectedScryfallId).unwrap();
          // Backend returns flat response — availableLanguages is at the top level
          const perSetLanguages = (cardData as any)?.availableLanguages as
            Array<{ lang: string; scryfallId: string }> | undefined;
          if (perSetLanguages && perSetLanguages.length > 0) {
            updates.availableLanguages = perSetLanguages;
            updates.scryfallId = selectedScryfallId;

            // Reset language if current language not available in new set
            const currentLangAvailable = perSetLanguages.some(
              l => l.lang === card.language
            );
            if (!currentLangAvailable) {
              const enVariant = perSetLanguages.find(l => l.lang === 'en');
              updates.language = enVariant ? 'en' : perSetLanguages[0]?.lang || 'en';
            }
          }
        } catch {
          // On failure, keep existing availableLanguages
        }
      }

      // Auto-switch foil if current selection becomes invalid
      const userAcceptsFoil = card.acceptFoil ?? true;
      const userAcceptsNonFoil = card.acceptNonFoil ?? true;
      const canOfferFoil = newAvailableInFoil && userAcceptsFoil;
      const canOfferNonFoil = newAvailableInNonFoil && userAcceptsNonFoil;

      if (card.foil && !canOfferFoil && canOfferNonFoil) {
        updates.foil = false;
      } else if (!card.foil && !canOfferNonFoil && canOfferFoil) {
        updates.foil = true;
      }

      fetchNewPrice(updates);
    }
  };

  // Handle foil change
  const handleFoilChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const requestedFoil = e.target.value === 'foil';

    // Validate against availability
    const physicallyAvailableInFoil = card.availableInFoil ?? false;
    const physicallyAvailableInNonFoil = card.availableInNonFoil ?? true;
    const userAcceptsFoil = card.acceptFoil ?? true;
    const userAcceptsNonFoil = card.acceptNonFoil ?? true;

    const canOfferFoil = physicallyAvailableInFoil && userAcceptsFoil;
    const canOfferNonFoil = physicallyAvailableInNonFoil && userAcceptsNonFoil;

    if (requestedFoil && !canOfferFoil) {
      toast.warning('This card is not available in foil');
      return;
    }
    if (!requestedFoil && !canOfferNonFoil) {
      toast.warning('This card is not available in non-foil');
      return;
    }

    fetchNewPrice({ foil: requestedFoil });
  };

  // Handle condition change
  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    fetchNewPrice({ condition: e.target.value });
  };

  // Handle language change — recalculate price and update image
  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value;
    const updates: Partial<SidebarTradeCard> = { language: newLang };

    // Update image from availableLanguages if present
    if (card.availableLanguages) {
      const langVariant = card.availableLanguages.find(l => l.lang === newLang);
      if (langVariant?.scryfallId) {
        updates.imageUrl = AssetUrls.cards.getCardImage(langVariant.scryfallId, 'normal');
      }
    }

    fetchNewPrice(updates);
  };

  // Determine foil availability for dropdown
  const physicallyAvailableInFoil = card.availableInFoil ?? false;
  const physicallyAvailableInNonFoil = card.availableInNonFoil ?? true;
  const userAcceptsFoil = card.acceptFoil ?? true;
  const userAcceptsNonFoil = card.acceptNonFoil ?? true;
  const canOfferFoil = physicallyAvailableInFoil && userAcceptsFoil;
  const canOfferNonFoil = physicallyAvailableInNonFoil && userAcceptsNonFoil;
  const showFoilDropdown = canOfferFoil && canOfferNonFoil;

  // Get available conditions
  const acceptableConditions = card.acceptableConditions || CONDITION_OPTIONS.map(c => c.value);
  const availableConditions = CONDITION_OPTIONS.filter(c => acceptableConditions.includes(c.value));

  // Determine if we should show set dropdown (using deduped count)
  const hasMultipleSets = dedupedSetDetails.length > 1;

  // Current set value for dropdown — match by setCode+cardId first, then by setCode alone
  const currentSetDetail = dedupedSetDetails.find(
    sd => sd.code === card.setCode && sd.cardId === card.cardId
  ) || dedupedSetDetails.find(sd => sd.code === card.setCode);

  const currentSetValue = currentSetDetail
    ? `${currentSetDetail.code}-${currentSetDetail.collectorNumber || ''}`
    : card.setCode;

  return (
    <div className={`trade-sidebar-card ${card.isInvalid ? 'invalid' : ''}`}>
      {/* Card thumbnail */}
      <div className="trade-sidebar-card-image">
        <FoilCardImage
          src={getSmallImageUrl(card.imageUrl)}
          alt={card.cardName}
          foil={card.foil}
          borderRadius="4px"
          style={{ width: '60px', height: '84px', objectFit: 'cover' }}
        />
      </div>

      {/* Card details */}
      <div className="trade-sidebar-card-details">
        {/* Card name */}
        <div className="trade-sidebar-card-name" title={card.cardName}>
          {card.cardName}
        </div>

        {/* Dropdowns row */}
        <div className="trade-sidebar-card-dropdowns">
          {/* Set dropdown */}
          {hasMultipleSets ? (
            <select
              value={currentSetValue}
              onChange={handleSetChange}
              className="trade-sidebar-select set-select"
              disabled={isCalculating}
            >
              {[...dedupedSetDetails]
                .sort((a, b) => {
                  const nameCompare = a.name.localeCompare(b.name);
                  if (nameCompare !== 0) return nameCompare;
                  const aNum = parseInt(a.collectorNumber || '0', 10);
                  const bNum = parseInt(b.collectorNumber || '0', 10);
                  return aNum - bNum;
                })
                .map(setDetail => {
                  const uniqueValue = `${setDetail.code}-${setDetail.collectorNumber || ''}`;
                  const displayText = setDetail.collectorNumber
                    ? `${setDetail.name} #${setDetail.collectorNumber}`
                    : setDetail.name;
                  return (
                    <option key={uniqueValue} value={uniqueValue}>
                      {displayText}
                    </option>
                  );
                })}
            </select>
          ) : (
            <span className="trade-sidebar-card-set-text">{card.set}</span>
          )}

          {/* Foil dropdown */}
          {showFoilDropdown ? (
            <select
              value={card.foil ? 'foil' : 'non-foil'}
              onChange={handleFoilChange}
              className="trade-sidebar-select foil-select"
              disabled={isCalculating}
            >
              <option value="non-foil">Non-Foil</option>
              <option value="foil">Foil</option>
            </select>
          ) : (
            <span className="trade-sidebar-card-foil-text">
              {canOfferFoil ? 'Foil' : 'Non-Foil'}
            </span>
          )}

          {/* Condition dropdown */}
          {availableConditions.length > 1 ? (
            <select
              value={card.condition}
              onChange={handleConditionChange}
              className="trade-sidebar-select condition-select"
              disabled={isCalculating}
            >
              {availableConditions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <span className="trade-sidebar-card-condition-text">
              {availableConditions[0]?.label || 'NM'}
            </span>
          )}

          {/* Language dropdown — filtered by availableLanguages and receiver's acceptableLanguages */}
          {(() => {
            let filteredLangs: Array<{value: string; label: string}>;

            if (card.availableLanguages && card.availableLanguages.length > 0) {
              // Use actual available languages for this printing
              let langs = card.availableLanguages;
              if (card.acceptableLanguages?.length) {
                langs = langs.filter(l =>
                  card.acceptableLanguages!.some(al => normalizeLanguageCode(al) === l.lang)
                );
              }
              filteredLangs = langs.map(l => ({
                value: l.lang,
                label: formatLanguage(l.lang),
              }));
            } else {
              // Fallback to LANGUAGE_OPTIONS
              filteredLangs = card.acceptableLanguages?.length
                ? LANGUAGE_OPTIONS.filter(opt =>
                    card.acceptableLanguages!.some(lang => normalizeLanguageCode(lang) === opt.value)
                  )
                : LANGUAGE_OPTIONS;
            }

            return filteredLangs.length > 1 ? (
              <select
                value={card.language}
                onChange={handleLanguageChange}
                className="trade-sidebar-select language-select"
                disabled={isCalculating}
              >
                {filteredLangs.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <span className="trade-sidebar-card-condition-text">
                {filteredLangs[0]?.label || card.language}
              </span>
            );
          })()}
        </div>

        {/* Price */}
        <div className="trade-sidebar-card-price">
          {isCalculating ? (
            <span className="trade-sidebar-card-price-loading">...</span>
          ) : (
            <Points points={card.pointValue} type="small" />
          )}
          {!isCalculating && card.languageMultiplierApplied && card.pointValue >= BULK_TIER_POINTS_THRESHOLD && (
            <span className="trade-sidebar-card-price-annotation" title="Non-English card values are estimated using market data and may not reflect your card's exact market price.">
              {getLanguageDisplayName(card.language)} pricing
            </span>
          )}
        </div>
      </div>

      {/* Remove button */}
      <button
        className="trade-sidebar-card-remove"
        onClick={() => onRemove(card.id)}
        aria-label="Remove card"
      >
        <img
          src={AssetUrls.icons.dropdowns.close}
          alt="Remove"
          className="trade-sidebar-card-remove-icon"
        />
      </button>

      {/* Invalid card overlay */}
      {card.isInvalid && (
        <div className="trade-sidebar-card-invalid-overlay">
          <span>No longer available</span>
        </div>
      )}
    </div>
  );
};

export default TradeSidebarCard;
